import {
  type QueryClient,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query'

import type { HotelsApi } from '../api/hotelsApi'
import { HOTELS_LARGE_LIST_LIMIT } from '../constants/limits'
import type { ApiError } from '../types/api'
import type { EnrichedRoom } from '../types/rooms'
import type { WakeupCall } from '../types/wakeup'
import { fetchAllPaginatedResults } from '../utils/paginatedFetch'
import {
  getRoomDashboardWakeups,
  getWakeupNotificationTone,
  type WakeupNotificationTone,
} from '../utils/wakeupDashboard'
import { HOTELS_QUERY_KEYS } from './queryKeys'

type RoomWakeupIdentity = Pick<
  EnrichedRoom,
  'id' | 'uuid' | 'extension_uuid' | 'number' | 'name' | 'wakeup_calls_count'
>

export interface RoomWakeupData {
  wakeups: WakeupCall[]
  tone: WakeupNotificationTone
  source: 'scoped' | 'fallback'
}

interface RoomWakeupQueryParams {
  api: HotelsApi
  pbxUuid: string
  room: RoomWakeupIdentity
  limit?: number
}

interface UseRoomWakeupDataParams extends RoomWakeupQueryParams {
  options?: Omit<
    UseQueryOptions<RoomWakeupData, ApiError>,
    'queryKey' | 'queryFn'
  >
}

const createRoomWakeupQueryKey = (pbxUuid: string, room: RoomWakeupIdentity) =>
  HOTELS_QUERY_KEYS.roomWakeups(
    pbxUuid,
    room.id,
    room.uuid,
    room.number,
    room.name,
    room.wakeup_calls_count,
  )

const fetchRoomWakeupData = async ({
  api,
  pbxUuid,
  room,
  limit,
}: RoomWakeupQueryParams): Promise<RoomWakeupData> => {
  const pageSize = limit ?? HOTELS_LARGE_LIST_LIMIT
  const scopedResponse = await api.listWakeupCalls({
    pbx_id: pbxUuid,
    extension_id: room.extension_uuid,
    limit: pageSize,
  })
  if (!scopedResponse.ok) {
    return Promise.reject(scopedResponse.data as ApiError)
  }

  const scopedResults = getRoomDashboardWakeups(
    scopedResponse.data!.results ?? [],
    room,
  )
  const shouldFallback =
    (room.wakeup_calls_count ?? 0) > 0 && scopedResults.length === 0
  if (!shouldFallback) {
    return {
      wakeups: scopedResults,
      tone: getWakeupNotificationTone(scopedResults),
      source: 'scoped',
    }
  }

  const allWakeups = await fetchAllPaginatedResults<WakeupCall, ApiError>({
    initialLimit: pageSize,
    fetchPage: ({ limit: pageLimit, offset }) =>
      api.listWakeupCalls({
        pbx_id: pbxUuid,
        limit: pageLimit,
        offset,
      }),
  })
  const fallbackResults = getRoomDashboardWakeups(
    allWakeups.results ?? [],
    room,
  )

  return {
    wakeups: fallbackResults,
    tone: getWakeupNotificationTone(fallbackResults),
    source: 'fallback',
  }
}

export const useRoomWakeupData = ({
  api,
  pbxUuid,
  room,
  limit,
  options,
}: UseRoomWakeupDataParams) => {
  const query = useQuery<RoomWakeupData, ApiError>({
    queryKey: createRoomWakeupQueryKey(pbxUuid, room),
    queryFn: () =>
      fetchRoomWakeupData({
        api,
        pbxUuid,
        room,
        limit,
      }),
    enabled: !!pbxUuid && !!room.extension_uuid && (options?.enabled ?? true),
    staleTime: options?.staleTime ?? 30_000,
    ...options,
    retry: options?.retry ?? false,
  })

  return {
    ...query,
    wakeups: query.data?.wakeups ?? [],
    tone: query.data?.tone ?? null,
    source: query.data?.source ?? 'scoped',
    isEmpty: !query.isLoading && (query.data?.wakeups?.length ?? 0) === 0,
  }
}

interface PrefetchRoomWakeupDataParams extends RoomWakeupQueryParams {
  queryClient: QueryClient
  staleTime?: number
}

export const prefetchRoomWakeupData = ({
  queryClient,
  api,
  pbxUuid,
  room,
  limit,
  staleTime = 30_000,
}: PrefetchRoomWakeupDataParams) => {
  if (!pbxUuid || !room.extension_uuid) return Promise.resolve(undefined)

  return queryClient.prefetchQuery<RoomWakeupData, ApiError>({
    queryKey: createRoomWakeupQueryKey(pbxUuid, room),
    queryFn: () =>
      fetchRoomWakeupData({
        api,
        pbxUuid,
        room,
        limit,
      }),
    staleTime,
  })
}
